/**
 * KAN-753: менеджер сессии - кто работает с клиентами в этом браузере до выхода
 * из кабинета. Хранится в localStorage под ключом с id пользователя:
 * { managerId, prompted }. prompted: окно выбора после входа уже показано
 * (выбрали или пропустили), до следующего входа оно не открывается.
 * Все обращения к хранилищу в try/catch: без хранилища кабинет работает как
 * "менеджер не выбран". Подписка нужна, чтобы шапка, окно и блок "Требуют
 * внимания" видели один и тот же выбор.
 */
import type { ManagerBrief } from '@/types/manager';

export const SESSION_MANAGER_KEY_PREFIX = 'sessionManager:';

export interface SessionManagerState {
  /** Выбранный менеджер; null: не выбран. */
  managerId: string | null;
  /** Окно выбора после входа уже показано. */
  prompted: boolean;
}

export const EMPTY_SESSION_MANAGER: SessionManagerState = Object.freeze({
  managerId: null,
  prompted: false,
});

type Listener = () => void;
const listeners = new Set<Listener>();

function notify(): void {
  listeners.forEach((listener) => listener());
}

export function sessionManagerKey(userId: string): string {
  return `${SESSION_MANAGER_KEY_PREFIX}${userId}`;
}

/** Сырая строка из хранилища (стабильный снимок для useSyncExternalStore). */
export function readSessionManagerRaw(userId: string): string | null {
  try {
    return window.localStorage.getItem(sessionManagerKey(userId));
  } catch {
    return null;
  }
}

/** Разбор сохранённого значения; мусор и отсутствие дают пустое состояние. */
export function parseSessionManagerState(raw: string | null): SessionManagerState {
  if (raw === null) return EMPTY_SESSION_MANAGER;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return EMPTY_SESSION_MANAGER;
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return EMPTY_SESSION_MANAGER;
  const { managerId, prompted } = value as Record<string, unknown>;
  if (typeof prompted !== 'boolean') return EMPTY_SESSION_MANAGER;
  if (managerId !== undefined && managerId !== null && typeof managerId !== 'string') {
    return EMPTY_SESSION_MANAGER;
  }
  return { managerId: managerId ?? null, prompted };
}

export function writeSessionManager(userId: string, state: SessionManagerState): void {
  try {
    window.localStorage.setItem(sessionManagerKey(userId), JSON.stringify(state));
  } catch {
    // хранилище недоступно: выбор не сохраняется, чтение вернёт пустое
    // состояние (менеджер не выбран, prompted=false)
  }
  notify();
}

/** KAN-925: стереть выбор одного пользователя (его вход и выход; другие аккаунты не трогаются). */
export function clearSessionManager(userId: string): void {
  try {
    window.localStorage.removeItem(sessionManagerKey(userId));
  } catch {
    // хранилище недоступно: стирать нечего
  }
  notify();
}

/** Стереть выбор всех пользователей этого браузера. */
export function clearSessionManagers(): void {
  try {
    const storage = window.localStorage;
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key?.startsWith(SESSION_MANAGER_KEY_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => storage.removeItem(key));
  } catch {
    // хранилище недоступно: стирать нечего
  }
  notify();
}

/** Подписка на изменения: в этой вкладке и в соседних (событие storage). */
export function subscribeSessionManager(listener: Listener): () => void {
  listeners.add(listener);
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key.startsWith(SESSION_MANAGER_KEY_PREFIX)) listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

/** Менеджер из текущего списка; удалённый или не выбранный: null. */
export function resolveSessionManager(
  managerId: string | null,
  managers: readonly ManagerBrief[]
): ManagerBrief | null {
  if (!managerId) return null;
  const found = managers.find((m) => m.id === managerId);
  return found ? { id: found.id, full_name: found.full_name } : null;
}
